// src/components/Apm/Tables/TableBasic.js

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { alpha, styled } from '@mui/material/styles';
import { LinearProgress, TableBody, TableSortLabel, useTheme, Button } from '@mui/material';
import { TableContainer, TableHead, Table, TableRow } from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Paper, Grid, useMediaQuery, Box, Menu } from '@mui/material';
import { TablePagination, Typography, MenuItem } from '@mui/material';
import { Popover, ListItemIcon } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IconButton } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { setPopupQuote, setPopupInv } from '../../../redux/slices/popupSlice';
import { setPopupTop } from '../../../redux/slices/popupTopSlice';
import { setFormTouchedQuote } from '../../../redux/slices/formTouchedSlice';
import { setFormTouchedInv } from '../../../redux/slices/formTouchedSlice';
import { getItems as getBankAccounts } from '../../../redux/slices/bankAccountSlice';
import { setSearchInv } from '../../../redux/slices/searchInvSlice';
import { setSearchQuote } from '../../../redux/slices/searchQuoteSlice';
import { getItems as getItemsForItemInv } from '../../../redux/slices/itemForItemInvSlice';
import { getContacts } from '../../../redux/slices/contactSlice';
import { getItems as getNoteTerms } from '../../../redux/slices/noteTermSlice';
import { getItems as getContractTerms } from '../../../redux/slices/contractTermSlice';
import { getItems as getCurrencies } from '../../../redux/slices/currencySlice';
import { getStatuses, setStatusesLocal } from '../../../redux/slices/statusSlice';
import { setStatusesLocalInv } from '../../../redux/slices/statusSlice';
import { appURL } from '../../Data/baseURL';
import ButtonAction from '../../Controls/Buttons/ButtonAction';
import ButtonBasic from '../../Controls/Buttons/ButtonBasic';
import ConfirmDialog from '../../Controls/Popups/ConfirmDialog';
import CSVLink from '../Other/CSVLink';
import { initialValuesSearchItem } from './InitialValues/initialValuesSearchItem';
import Inv from '../Inv/Inv';
import SearchItem from '../Search/SearchItem';
import PopupQuote from '../../Controls/Popups/PopupQuote';
import PopupInv from '../../Controls/Popups/PopupInv';
import QuickSearchInput from '../../Controls/Inputs/QuickSearchInput';
import Quote from '../Quote/Quote';
import { pages } from '../../Data/generalData';
import { statusesInDiffLang } from '../../Data/generalData';
import { useDeleteInvMutation } from '../../../redux/features/invApi';

const MuiAccordionSummaryStyled = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
}));

const TableStyled = styled(Table)(({ theme }) => ({
  marginTop: theme.spacing(6 / 4),
  '& thead th': {
    fontWeight: '500',
    color: theme.palette.primary.dark,
    backgroundColor: '#bdd600',
  },
  '& tbody th': {
    fontWeight: '300', // because of Typography use it has no action
  },
  '& tbody tr:hover': {
    backgroundColor: '#fffbf2',
    cursor: 'pointer',
  },
}));

const TableRowStyled = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableCellStyled = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const TypographyPopover = styled(Typography)(({ theme }) => ({
  padding: 16,
  color: 'rgba(0, 0, 0, 0.87)',
  maxWidth: 350,
  fontSize: theme.typography.pxToRem(20),
}));

const MenuStyled = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(0),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const MenuActionsStyled = styled((props) => (
  <Menu
    elevation={2}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(() => ({
  '& .MuiPaper-root': {},
}));

const TypographyStyled = styled(Typography)(() => ({
  '&.MuiTypography-root': { fontWeight: '500' },
}));

const getStatusColor = (serialNumber) => {
  switch (String(serialNumber ?? '')) {
    case '105':
    case '210':
      return 'primary';

    case '110':
    case '215':
      return 'success';

    case '115':
    case '205':
      return 'warning';

    case '220':
      return 'error';

    default:
      return 'inherit';
  }
};

const TableBasic = ({
  columns,
  items = [],
  totalCount = items.length,
  resourceType,
  isFetching,
  isDeleting: isDeletingProp = false,
  refetchItems,
  onDeleteItem,
  page = 0,
  rowsPerPage = pages[1],
  order = 'asc',
  orderBy,
  quickSearchInput = '',
  onQuickSearchChange,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const isQuotesPage = resourceType === 'quote';
  const isInvoicesPage = resourceType === 'invoice';
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const theme = useTheme();
  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));
  const smallMedia = useMediaQuery('(max-width:1075px)');
  const { popupQuote, popupInv } = useSelector((state) => state.popupSlice);
  const [, setSearchItem] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    message: '',
    type: '',
  });

  const { searchQuote } = useSelector((state) => state.searchQuoteSlice);
  const { searchInv, allowedToAddInv, allowedToAddQuote } = useSelector((state) => state.searchInvSlice);
  const allowedToAdd = isQuotesPage ? allowedToAddQuote : allowedToAddInv;
  const [expanded, setExpanded] = useState(false);
  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const [rowSelected, setRowSelected] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const { contacts, statusContacts } = useSelector((state) => state.contactSlice);
  const { bankAccounts, statusBankAccounts } = useSelector((state) => state.bankAccountSlice);
  const { itemsForItemInv, statusItemsForItemInv } = useSelector((state) => state.itemForItemInvSlice);
  const { noteTerms, statusNoteTerms } = useSelector((state) => state.noteTermSlice);
  const { contractTerms, statusContractTerms } = useSelector((state) => state.contractTermSlice);

  const { currencies, statusCurrencies } = useSelector((state) => state.currencySlice);
  const { statuses, statusStatuses, statusesLocal, statusesLocalInv } = useSelector((state) => state.statusSlice);

  const [deleteInvoiceOrQuote, { isLoading: isDeletingLocally }] = useDeleteInvMutation();
  const isDeleting = isDeletingProp || isDeletingLocally;

  ////// Start Popover
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClickPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  ////// End Popover

  ////// GET Contacts, bankAccounts, itemsForItemInv, modes, statuses ...
  useEffect(() => {
    batch(() => {
      if (contacts.length < 1 && statusContacts === null) {
        dispatch(getContacts('/'));
      }
      if (bankAccounts.length < 1 && statusBankAccounts === null) {
        dispatch(getBankAccounts('/'));
      }
      if (itemsForItemInv.length < 1 && statusItemsForItemInv === null) {
        dispatch(getItemsForItemInv('/'));
      }
      if (noteTerms.length < 1 && statusNoteTerms === null) {
        dispatch(getNoteTerms('/'));
      }
      if (contractTerms.length < 1 && statusContractTerms === null) {
        dispatch(getContractTerms('/'));
      }
      if (currencies.length < 1 && statusCurrencies === null) {
        dispatch(getCurrencies('/'));
      }

      if (statuses.length < 1 && statusStatuses === null) {
        dispatch(getStatuses());
      }
    });
  }, [
    contacts,
    bankAccounts,
    itemsForItemInv,
    noteTerms,
    contractTerms,
    currencies,
    statuses,
    statusContacts,
    statusItemsForItemInv,
    statusNoteTerms,
    statusContractTerms,
    statusCurrencies,
    statusBankAccounts,
    statusStatuses,
    dispatch,
  ]);

  ////// SetSearchItem
  useEffect(() => {
    searchInv !== null && setSearchItem(searchInv);
  }, [searchInv]);

  ////// Localize Modes, Statuses
  useEffect(() => {
    const lang = localStorage.getItem('i18nextLng' || 'ro');

    const statusesQuote = statuses.filter((status) => status.serial_number >= 100 && status.serial_number < 200);

    const statusesInv = statuses.filter((status) => status.serial_number >= 200);

    let statusesInAppLang = null;
    let statusesInAppLangInv = null;

    if (lang === 'en') {
      statusesInAppLang = statusesInDiffLang().slice(0, 4);
      statusesInAppLangInv = statusesInDiffLang().slice(12, 17);
    }

    if (lang === 'ro') {
      statusesInAppLang = statusesInDiffLang().slice(4, 8);
      statusesInAppLangInv = statusesInDiffLang().slice(17, 22);
    }

    if (lang === 'ru') {
      statusesInAppLang = statusesInDiffLang().slice(8, 12);
      statusesInAppLangInv = statusesInDiffLang().slice(22, 27);
    }

    let statusesLocal = [];
    let statusesLocalInv = [];

    statusesQuote.map((status, i) => {
      statusesLocal.push({ ...status, type: statusesInAppLang[i] });
      return null;
    });

    statusesInv.map((status, i) => {
      statusesLocalInv.push({ ...status, type: statusesInAppLangInv[i] });
      return null;
    });

    dispatch(setStatusesLocal(statusesLocal));
    dispatch(setStatusesLocalInv(statusesLocalInv));
  }, [dispatch, statuses]);

  ////// Start selectmenu email & print
  const [anchorElEmail, setAnchorElEmail] = React.useState(null);
  const openSelectButtonEmail = Boolean(anchorElEmail);
  const handleClickExport = (event) => {
    setAnchorElEmail(event.currentTarget);
  };
  const handleCloseEmail = () => {
    setAnchorElEmail(null);
  };
  ////// End selectmenu email & print

  ////// Start actions MenuList
  const [itemActions, setItemActions] = useState(null);
  const [anchorEl7, setAnchorEl7] = useState(null);
  const open7 = Boolean(anchorEl7);
  const handleClickActions = (event) => {
    setAnchorEl7(event.currentTarget);
  };
  const handleCloseMenuActions = () => {
    setAnchorEl7(null);
  };
  ////// End actions MenuList

  ////// Start create list for CSV export
  const headersForCSV = [
    { label: 'entry_id', key: 'entry_id' },
    { label: 'quote_number', key: 'quote_number' },
    { label: 'invoice_number', key: 'invoice_number' },
    { label: 'date_issue', key: 'date_issue' },
    { label: 'invoice_to_fisc_code', key: 'invoice_to_fisc_code' },
    { label: 'invoice_to_company_name', key: 'invoice_to_company_name' },
    {
      label: 'invoice_to_country_domicile',
      key: 'invoice_to_country_domicile',
    },
    { label: 'comments', key: 'comments' },
    { label: 'load_detail', key: 'load_detail' },
    { label: 'load_address', key: 'load_address' },
    { label: 'unload_address', key: 'unload_address' },
    { label: 'invoice_currency', key: 'invoice_currency' },
    { label: 'article_line_id', key: 'article_line_id' },
    { label: 'article_description', key: 'article_description' },
    { label: 'article_unique_id', key: 'article_unique_id' },
    { label: 'article_code', key: 'article_code' },
    { label: 'article_quantity', key: 'article_quantity' },
    { label: 'article_amount', key: 'article_amount' },
    { label: 'article_vat', key: 'article_vat' },
  ];
  const getItemsForCSV = () => {
    let arrayForCSV = [];
    itemsAfterPagingAndSorting().map((item) => {
      item.iteminv_invs.map((iteminv) => {
        arrayForCSV.push({
          entry_id: item.id,
          quote_number: item.qn,
          invoice_number: item.vn,
          date_issue: item.date_inv,
          invoice_currency: item.currency && (item.currency.currency_numeric || ''),
          invoice_to_fisc_code:
            item.bill_to && item.bill_to.countrycodelegal
              ? item.bill_to.countrycodelegal.value === 'MD'
                ? item.bill_to.fiscalcode
                : item.bill_to.id + 77700000
              : '',
          invoice_to_company_name: item.bill_to && item.bill_to.companyname,
          invoice_to_country_domicile:
            item.bill_to && item.bill_to.countrycodelegal && (item.bill_to.countrycodelegal.value || ''),
          comments: item.comment_invs[0] ? item.comment_invs[0].comment || '' : '',
          load_detail: item.load_detail || '',
          load_address: item.load_address || '',
          unload_address: item.unload_address || '',
          article_line_id: iteminv.id,
          article_unique_id: iteminv.item_for_item_inv && iteminv.item_for_item_inv.id,
          article_code: iteminv.item_for_item_inv && iteminv.item_for_item_inv.code,
          article_quantity: iteminv.quantity || 1,
          article_description: iteminv.item_for_item_inv && iteminv.item_for_item_inv.description,
          article_amount: iteminv.amount,
          article_vat: iteminv.item_for_item_inv && iteminv.item_for_item_inv.vat,
        });

        return null;
      });

      return null;
    });

    return arrayForCSV;
  };
  ////// End create list for CSV export

  const handleClickRow = (i) => {
    setRowSelected(i || i === 0 ? i : null);
  };

  const openInPopup = (item) => {
    setSelectedItem(item);

    if (isQuotesPage) {
      dispatch(setFormTouchedQuote(false));

      dispatch(setPopupQuote(true));

      return;
    }

    if (isInvoicesPage) {
      dispatch(setFormTouchedInv(false));

      dispatch(setPopupInv(true));
    }
  };

  const handleCreate = () => {
    setSelectedItem({});

    if (isQuotesPage) {
      dispatch(setFormTouchedQuote(false));

      dispatch(setPopupQuote(true));

      return;
    }

    if (isInvoicesPage) {
      dispatch(setFormTouchedInv(false));

      dispatch(setPopupInv(true));
    }
  };

  const handleCopy = (event, item) => {
    event.preventDefault();
    event.stopPropagation();

    if (!item) {
      return;
    }

    const dateInv = new Date().toISOString().split('T')[0];

    const entryInvs = (item.entry_invs ?? []).map((entry) => ({
      ...entry,
      id: undefined,

      entrydetails: (entry.entrydetails ?? []).map((detail) => ({
        ...detail,
        id: undefined,
      })),
    }));

    const itemInvs = (item.iteminv_invs ?? []).map((entry) => ({
      ...entry,
      id: undefined,
    }));

    const comments = (item.comment_invs ?? []).map((comment) => ({
      ...comment,
      id: undefined,
    }));

    const copiedItem = {
      ...item,

      id: '',
      uf: '',
      vn: '',
      qn: '',
      date_inv: dateInv,

      entry_invs: entryInvs,
      iteminv_invs: itemInvs,
      comment_invs: comments,

      history_invs: [],
      status: '',
    };

    setSelectedItem(copiedItem);

    if (isQuotesPage) {
      dispatch(setFormTouchedQuote(false));

      dispatch(setPopupQuote(true));

      return;
    }

    if (isInvoicesPage) {
      dispatch(setFormTouchedInv(false));

      dispatch(setPopupInv(true));
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      toast.error(() => t('toast.invoice_could_not_be_found'));
      return;
    }

    try {
      if (onDeleteItem) {
        await onDeleteItem(id);
      } else {
        await deleteInvoiceOrQuote(id).unwrap();
      }

      dispatch(setPopupTop(false));

      toast.success(() => t(isQuotesPage ? 'toast.quote_successfully_deleted' : 'toast.invoice_successfully_deleted'));
    } catch (error) {
      console.error('Delete failed:', error);

      toast.error(() => t(isQuotesPage ? 'toast.quote_could_not_be_deleted' : 'toast.invoice_could_not_be_deleted'));
    }
  };

  const handleRefresh = async () => {
    if (!refetchItems) {
      return;
    }

    try {
      await refetchItems().unwrap();

      toast.success(() => t(isQuotesPage ? 'toast.quote_list_updated' : 'toast.invoice_list_updated'));
    } catch (error) {
      console.error('Refresh failed:', error);

      toast.error(() => t(isQuotesPage ? 'toast.quote_list_not_updated' : 'toast.invoice_list_not_updated'));
    }
  };

  const handleQuickSearch = (e) => {
    onQuickSearchChange?.(e?.target?.value ?? '');
  };

  const clearQuickSearch = () => {
    onQuickSearchChange?.('');
  };

  const handleChangePage = (_, newPage) => {
    onPageChange?.(_, newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onRowsPerPageChange?.(event);
  };

  const handleSortRequest = (cellId) => {
    onSortChange?.(cellId);
  };

  const itemsAfterPagingAndSorting = () => {
    return items;
  };

  ////// Start empty row generation
  const emptyRows = Math.min(rowsPerPage, Math.abs(itemsAfterPagingAndSorting().length - rowsPerPage));
  let emptyRowsArray = Array.from(Array(emptyRows)).map((_, index) => index);
  ////// End empty row generation

  // const tableItems = items;
  // const itemCount = items.length;

  return (
    <>
      <MuiAccordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')} sx={{ width: '100%' }}>
        <MuiAccordionSummaryStyled
          id="panel1bh-header"
          aria-controls="panel1bh-content"
          expandIcon={<ExpandMoreIcon />}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
          }}
        >
          <Grid container item xs={10.5}>
            <FilterAltIcon color="primary" sx={{ mr: 3 }} />
            {((pathname === `${appURL}/quotes` &&
              searchQuote &&
              Object.keys(searchQuote).some(function (k) {
                return searchQuote[k] !== '' && searchQuote[k] !== null;
              })) ||
              (pathname === `${appURL}/invoices` &&
                searchInv &&
                Object.keys(searchInv).some(function (k) {
                  return searchInv[k] !== '' && searchInv[k] !== null;
                }))) && (
              <Button
                color="secondary"
                sx={{ minWidth: 130, p: 0, m: 0 }}
                size="string"
                endIcon={<FilterAltOffIcon />}
                onClick={() => [
                  setTimeout(() => setExpanded(false), 40),
                  setExpanded(false),
                  pathname === `${appURL}/quotes` && dispatch(setSearchQuote(initialValuesSearchItem)),
                  pathname === `${appURL}/invoices` && dispatch(setSearchInv(initialValuesSearchItem)),
                ]}
              >
                <Typography variant="button" sx={{ pl: 0, pr: 0 }}>
                  {t('button.clear_filter')}
                </Typography>
              </Button>
            )}
          </Grid>
        </MuiAccordionSummaryStyled>

        <MuiAccordionDetails sx={{ pr: 0, pl: 0, pb: 0 }}>{expanded && <SearchItem />}</MuiAccordionDetails>
      </MuiAccordion>

      <Grid component={Paper} container item sx={{ mt: 2 }}>
        {/* Add button */}
        <Grid item sm={6} xs={12}>
          <ButtonAction
            color="primary"
            variant="outlined"
            fullWidth={smMediaDown ? true : false}
            onClick={allowedToAdd ? handleCreate : handleClickPopover}
          >
            {isQuotesPage ? t('button.add_quote') : t('button.add_invoice')}
          </ButtonAction>

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <TypographyPopover>
              {isQuotesPage
                ? t('toast.need_to_upgrade_in_order_to_register_new_quotes')
                : t('toast.need_to_upgrade_in_order_to_register_new_invoices')}
            </TypographyPopover>
          </Popover>
        </Grid>

        {/* Quick search & refresh */}
        <Grid item sm={6} xs={12} sx={{ mt: smMediaDown ? 1.5 : 0 }}>
          <QuickSearchInput
            quickSearchInput={quickSearchInput}
            handleQuickSearch={handleQuickSearch}
            clearQuickSearch={clearQuickSearch}
            handleRefresh={handleRefresh}
          />
        </Grid>

        {/* Table */}
        <Grid container item xs={12} sx={{ mt: 1 }}>
          {isFetching && <LinearProgress aria-label={t('public.loading')} sx={{ width: '100%' }} />}
          <TableContainer aria-busy={Boolean(isFetching)}>
            <TableStyled
              stickyHeader
              size="small"
              sx={{
                '& thead th':
                  pathname === '/apps/quotes'
                    ? {
                        fontWeight: '500',
                        color: theme.palette.primary.dark,
                        backgroundColor: '#ffd827',
                      }
                    : undefined,
              }}
            >
              <TableHead>
                <TableRowStyled>
                  {columns.map((column, index) => (
                    <TableCell
                      key={index}
                      align={column.align}
                      // sortDirection={orderBy === column.id ? order : false}
                      sx={{
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        px: smMediaDown ? 0.75 : undefined,
                      }}
                    >
                      <Typography noWrap whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden" fontWeight="500">
                        {column.disableSorting ? (
                          column.label
                        ) : (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => {
                              handleSortRequest(column.id);
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        )}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRowStyled>
              </TableHead>
              <TableBody>
                {itemsAfterPagingAndSorting().map((row, index) => {
                  return (
                    <TableRowStyled
                      key={row.uf ?? row.id ?? index}
                      id={row.uf ?? row.id}
                      sx={{ cursor: 'pointer' }}
                      style={{
                        backgroundColor: rowSelected === index ? '#fffbf2' : undefined,
                        height: 37.5,
                      }}
                      onClick={() => handleClickRow(index)}
                      onDoubleClick={() => openInPopup(row)}
                    >
                      {columns.map((column, ind) => {
                        const statusColor = getStatusColor(row.status?.serial_number);

                        return (
                          <TableCellStyled
                            key={ind}
                            align={column.align}
                            sx={{
                              minWidth: column.minWidth,
                              maxWidth: column.maxWidth,
                              px: smMediaDown ? 0.75 : undefined,
                            }}
                          >
                            <Typography noWrap fontWeight="450">
                              {column.id === 'vn' && row.vn}

                              {column.id === 'qn' && row.qn}

                              {column.id === 'date_inv' &&
                                row.date_inv &&
                                new Date(row.date_inv).toLocaleDateString('en-GB')}
                              {column.id === 'date_due' &&
                                row.date_due &&
                                new Date(row.date_due).toLocaleDateString('en-GB')}

                              {column.id === 'load_detail' && row.load_detail && row.load_detail}

                              {column.id === 'load_address' && row.load_address && row.load_address}

                              {column.id === 'load_place' &&
                                row.entry_invs &&
                                row.entry_invs[0] &&
                                row.entry_invs[0].country_load &&
                                row.entry_invs[0].country_load.value + ' ' + (row.entry_invs[0].city_load || '')}

                              {column.id === 'unload_address' && row.unload_address && row.unload_address}

                              {column.id === 'unload_place' &&
                                row.entry_invs &&
                                row.entry_invs[0] &&
                                row.entry_invs[0].country_unload &&
                                row.entry_invs[0].country_unload.value + ' ' + (row.entry_invs[0].city_unload || '')}

                              {column.id === 'amount' &&
                                (row.iteminv_invs &&
                                  row.iteminv_invs
                                    .map((item, i) => {
                                      let a = item.amount;
                                      let b = item.quantity || 1;
                                      let c =
                                        item.item !== null &&
                                        a * b * (item.item_for_item_inv && item.item_for_item_inv.vat / 100);
                                      return a * b + c;
                                    })
                                    .reduce((prev, cur) => {
                                      return prev + cur;
                                    }, 0)
                                    .toFixed(2)) +
                                  '  ' +
                                  (row.currency ? row.currency.currency_code : '')}

                              {(column.id === 'bill_to' || column.id === 'quote_to') && row.bill_to
                                ? row.bill_to.companyname
                                : ''}

                              {column.id === 'comment_invs' && row.comment_invs[0] && row.comment_invs[0].comment}

                              {column.id === 'status' && row.status && row.status.id && statusesLocal.length > 1 ? (
                                <>
                                  <FiberManualRecordIcon
                                    color={statusColor}
                                    fontSize="small"
                                    style={{
                                      maxHeight: '17px',
                                      minHeight: '17px',
                                    }}
                                    sx={{ pt: 0.5, pr: 0.5 }}
                                  />
                                  {(pathname === '/apps/quotes' ? statusesLocal : statusesLocalInv).find(
                                    (status) => status.id === row.status.id
                                  )?.type || ''}
                                </>
                              ) : (
                                ''
                              )}

                              {column.id === 'actions' && (
                                <>
                                  <IconButton
                                    id="positioned-button"
                                    aria-controls={open7 ? 'positioned-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open7 ? 'true' : undefined}
                                    variant="contained"
                                    size="small"
                                    color="primary"
                                    sx={{ py: 0 }}
                                    onClick={(e) => [handleClickActions(e), setItemActions(row)]}
                                  >
                                    <MoreHorizIcon />
                                  </IconButton>

                                  <MenuActionsStyled
                                    id="positioned-menu"
                                    aria-labelledby="positioned-button"
                                    anchorEl={anchorEl7}
                                    open={open7}
                                    onClose={handleCloseMenuActions}
                                    PaperProps={{
                                      sx: {
                                        overflow: 'visible',
                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.10))',
                                        mt: 3,
                                        '& .MuiAvatar-root': {
                                          width: 45,
                                          height: 45,
                                          ml: -0.5,
                                          mr: 1,
                                          mb: 0,
                                        },
                                        '&:before': {
                                          content: '""',
                                          display: 'block',
                                          position: 'absolute',
                                          top: 0,
                                          right: 14,
                                          width: 10,
                                          height: 10,
                                          bgcolor: 'background.paper',
                                          transform: 'translateY(-50%) rotate(45deg)',
                                          zIndex: 0,
                                        },
                                        minWidth: 250,
                                      },
                                    }}
                                  >
                                    <MenuItem
                                      onClick={() => [handleCloseMenuActions(), openInPopup(itemActions)]}
                                      disableRipple
                                    >
                                      <ListItemIcon>
                                        <EditIcon />
                                      </ListItemIcon>
                                      <TypographyStyled>{t('button.edit_small')}</TypographyStyled>
                                    </MenuItem>

                                    <MenuItem
                                      onClick={(e) => [
                                        handleCloseMenuActions(),
                                        !allowedToAddInv && pathname !== '/apps/quotes'
                                          ? handleClickPopover
                                          : handleCopy(e, itemActions),
                                      ]}
                                      disableRipple
                                    >
                                      <ListItemIcon>
                                        <FileCopyIcon />
                                      </ListItemIcon>
                                      <TypographyStyled>{t('button.copy_small')}</TypographyStyled>
                                    </MenuItem>

                                    <Divider sx={{ my: 0.5 }} />

                                    <MenuItem
                                      disabled={isDeleting}
                                      onClick={() => {
                                        handleCloseMenuActions();

                                        setConfirmDialog({
                                          isOpen: true,
                                          title: t('popup.are_you_sure_to_delete_record'),
                                          subTitle: t('popup.you_can_not_undo_this_operation'),
                                          onConfirm: () => handleDelete(itemActions?.uf ?? itemActions?.id),
                                        });
                                      }}
                                    >
                                      <ListItemIcon>
                                        <DeleteIcon color="error" />
                                      </ListItemIcon>
                                      <TypographyStyled color="error">{t('button.delete_small')}</TypographyStyled>
                                    </MenuItem>
                                  </MenuActionsStyled>
                                </>
                              )}
                            </Typography>
                          </TableCellStyled>
                        );
                      })}
                    </TableRowStyled>
                  );
                })}

                {emptyRowsArray.map((_, ind) => {
                  return (
                    <TableRowStyled
                      key={110 + ind}
                      style={{
                        height: 37.5,
                      }}
                    >
                      <TableCellStyled key={150 + ind} colSpan={10}></TableCellStyled>
                    </TableRowStyled>
                  );
                })}
              </TableBody>
            </TableStyled>
            <Box
              sx={{
                width: '100%',
                display: 'inline-flex',
                justifyContent: 'space-between',
              }}
            >
              <TablePagination
                component={Box}
                rowsPerPageOptions={pages}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              <Box sx={{ alignSelf: 'center', mr: smallMedia ? null : 1.5 }}>
                <ButtonBasic
                  id="demo-customized-button"
                  color="primary"
                  sx={{
                    ml: smallMedia ? null : 2,
                  }}
                  aria-controls={openSelectButtonEmail ? 'demo-customized-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openSelectButtonEmail ? 'true' : undefined}
                  disableElevation
                  onClick={handleClickExport}
                  endIcon={smMediaDown ? undefined : <KeyboardArrowDownIcon />}
                >
                  {smMediaDown ? <IosShareIcon /> : <Typography variant="button">{t('button.export')}</Typography>}
                </ButtonBasic>
                <MenuStyled
                  id="demo-customized-menu"
                  MenuListProps={{
                    'aria-labelledby': 'demo-customized-button',
                  }}
                  anchorEl={anchorElEmail}
                  open={openSelectButtonEmail}
                  onClose={handleCloseEmail}
                  onClick={() => setAnchorElEmail(false)}
                >
                  <CSVLink type="button" disableRipple data={getItemsForCSV} headers={headersForCSV}></CSVLink>
                </MenuStyled>
              </Box>
            </Box>
          </TableContainer>
        </Grid>
      </Grid>

      {popupQuote && isQuotesPage && (
        <PopupQuote
          title={`${t(smMediaDown ? 'quote.quote_hash_short' : 'quote.quote_hash')} ${selectedItem?.qn || ''}`}
        >
          <Quote initialValue={selectedItem} />
        </PopupQuote>
      )}

      {popupInv && isInvoicesPage && (
        <PopupInv title={`${t(smMediaDown ? 'inv.invoice_hash_short' : 'inv.invoice_hash')} ${selectedItem?.vn || ''}`}>
          <Inv initialValue={selectedItem} />
        </PopupInv>
      )}

      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog}></ConfirmDialog>
    </>
  );
};

export default TableBasic;
